/*
 * The boot screen (#135).
 *
 * Flat fields and rules, as everywhere. No gradients — which is worth saying
 * out loud on this file in particular, because the two devices a loading screen
 * reaches for by reflex are a gradient shimmer and a glowing bar, and both are
 * forbidden by section 01.
 */

/*
 * The window, exactly — never more of it, and never less.
 *
 * **You do not scroll a loading screen.** It is not a document, it has no
 * second half, and a scrollbar down the side of one is an offer to go and look
 * at something that does not exist. So this is the viewport and nothing but:
 * fixed, `100dvh` tall, and clipped.
 *
 * It was `min-height` in normal flow, which is what `.loaderPage` does and is
 * right for that component — a mark centred in a page has nothing under it to
 * push past the fold. This has a footer, and the footer plus a wordmark plus a
 * 5rem mark is enough to overflow a short window; `min-height` then lets the
 * document grow and the screen becomes scrollable.
 *
 * `100dvh` rather than the `bottom: 0` that `inset` would give, and the two are
 * not the same on a phone: a fixed element resolves against the large viewport,
 * so with the browser's toolbars showing its bottom edge sits behind them. The
 * dynamic unit is the one that means *the height you can actually see*.
 *
 * Two rows. The mark takes what is left after the footer has what it needs,
 * which is what makes this fit at any height without arithmetic — see `.hero`
 * for which of the two gives way when there is genuinely not enough room.
 *
 * The ground and every role token come from `[data-gb-space]` in `tokens.css`,
 * so a studio boots into the developers ground and a creator into theirs. That
 * attribute is on this element rather than an ancestor because there is no
 * ancestor: `Page` has not rendered and may never — this is what a session
 * check draws while it decides whether it will.
 */
.boot {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100dvh;
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
  /*
    Above everything, though nothing is under it today: this renders *instead
    of* the application, before the router reaches the providers. The value is
    past `.loaderOver`'s 40 so that the two can never argue if a boot screen is
    ever drawn over a page.
  */
  z-index: 60;
}

/*
 * The mark, the wordmark and the status line, centred in whatever is left.
 *
 * `min-height: 0` is what lets the `1fr` row shrink below its content: a grid
 * track's default minimum is its content size, so without it a tall footer on a
 * short window pushes the total past `100dvh` and the clip eats the bar rather
 * than the row that could have given way.
 *
 * **When there is genuinely not enough room, this is what loses, and that is
 * the deliberate direction.** A tip clipped mid-sentence is a tip somebody
 * stops reading; a mark clipped through the middle is the product looking
 * broken on the first screen anybody sees of it. So the footer keeps its height
 * and the space around the mark closes up — and the mark itself is 5rem, which
 * fits in every window a browser can be made into.
 */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--gb-space-6);
  padding: var(--gb-space-6) var(--gb-gutter);
  text-align: center;
  min-height: 0;
  overflow: hidden;
}

/*
 * A short window gets a smaller wordmark and less air.
 *
 * A laptop with the browser at half height, or a phone turned sideways. The
 * mark does not shrink — it is the identity and it is only 5rem — but the
 * wordmark under it is set from the *width* of the viewport, which on a
 * sideways phone is large exactly when the height is not.
 */
@media (max-height: 34rem) {
  .hero {
    gap: var(--gb-space-3);
    padding: var(--gb-space-4) var(--gb-gutter);
  }

  .lockup {
    gap: var(--gb-space-3);
  }

  .word {
    font-size: clamp(1.5rem, 5vw, 2.25rem);
  }
}

/*
 * The lockup: the running mark, with the wordmark under it.
 *
 * The vertical arrangement `Logo` already uses, with one substitution — the
 * mark is the *running* one rather than the static one. It is the same
 * silhouette at the same proportions, so what is on screen is the identity
 * lockup with a light travelling up it, not a spinner that happens to be near a
 * logo.
 */
.lockup {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gb-space-6);
  color: var(--gb-text);
}

/*
 * The wordmark at boot size.
 *
 * Only `font-size` is set here, which is the one thing `Logo` parameterises
 * too. Everything else — the width and weight axes, the tracking, the case —
 * stays with `.word` in `brand.module.css`, because section 02 is explicit that
 * the wordmark is never restyled, and a second opinion about it living in this
 * file is exactly how that rule gets broken.
 */
.word {
  font-size: clamp(2rem, 8vw, 4rem);
}

/*
 * What is being waited for, in words.
 *
 * The mark is `aria-hidden`; this is the part a screen reader hears, and
 * `BootScreen` gives it `role="status"`. Data-label treatment rather than body
 * type: it is a machine state, not a sentence addressed to anybody.
 */
.status {
  margin: 0;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-muted);
}

/*
 * The bottom of the screen: the tip, and the bar.
 *
 * **Its height is reserved from the first frame, whether there is a tip in it
 * or not.** The tip arrives later than the mark does — see `TIP_MS` in the
 * app's `loading.tsx` — and is replaced every few seconds after that, so it is
 * the one piece of this screen whose height moves while somebody is looking at
 * it. Reserving the room means the mark above is centred in the same space
 * throughout: nothing jumps when the tip appears, and nothing jumps when a
 * two-line tip is replaced by a one-line one.
 *
 * It was out of flow and pinned to the bottom, which achieved the same thing
 * and could not be kept: an absolutely positioned footer contributes no height,
 * so the room for it had to be a guess written as padding on the container —
 * and a guess is exactly what a screen that must fit the viewport exactly
 * cannot afford. In flow, the grid reserves precisely what it takes.
 *
 * `justify-content: flex-end` so the sweep stays welded to the bottom edge
 * while the band above it grows upward into the reserved space.
 *
 * The reservation shrinks on a short window rather than eating it: `30dvh` on
 * anything under about 28rem tall, the full measure above that. Enough for the
 * label and two lines at a reading width.
 */
.foot {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: min(8.5rem, 30dvh);
}

/* The rule the tip hangs under, full width; the tip itself is measured. */
.tipBand {
  border-top: 1px solid var(--gb-rule);
}

.tip {
  margin: 0 auto;
  max-width: 48rem;
  padding: var(--gb-space-6) var(--gb-gutter);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  /* The screen above is centred; a paragraph is read from its left edge. */
  text-align: left;
  animation: gb-boot-tip var(--gb-motion-slow) var(--gb-ease-out) both;
}

@keyframes gb-boot-tip {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.tipLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-accent);
}

.tipText {
  font-family: var(--gb-font-text);
  font-size: 1rem;
  line-height: 1.5;
  color: var(--gb-text);
}

/*
 * The bar that never fills.
 *
 * A games loading screen has a bar across the bottom, and this one is honest
 * about the fact that it has no idea how long the wait is: a flat block sweeps
 * across a track and leaves the other side, over and over. It never reaches an
 * end, because there is no end to reach — a session check is a single round
 * trip with no measurable progress in it.
 *
 * **This is not a progress strip and must never become one.** Section 03's
 * device is bars whose heights or fills mean something — review scores by
 * version, or, since the amendment, a bounded process with a known end. A bar
 * that filled from left to right here would be claiming exactly that, and the
 * only number available to fill it with would be one this file invented. So the
 * shape is deliberately the other one: a single block in transit, at a constant
 * width, that is plainly travelling rather than accumulating.
 *
 * Full bleed and flush with the bottom edge, which is the one place on a screen
 * where a bar reads as furniture rather than as a control somebody could drag.
 */
.bar {
  height: 0.5rem;
  background: var(--gb-rule);
  overflow: hidden;
}

/*
 * `linear`, and it is the only motion in the system that is.
 *
 * The eases in `tokens.css` all describe something arriving or settling, and
 * both ends of this animation happen off the edge of the track where an ease
 * cannot be seen — what an ease *would* be visible as is the block slowing
 * through the middle of its travel, which reads as the machine hesitating. A
 * constant sweep reads as a machine working.
 *
 * -100% clears the left edge (the block's own width), and 357% is 100/28 of it,
 * which clears the right. Both are `transform`, so the sweep does not lay
 * anything out — a bar animating `left` at the bottom of every page load is a
 * reflow per frame for as long as the wait lasts.
 */
.bar i {
  display: block;
  height: 100%;
  width: 28%;
  background: var(--gb-accent);
  animation: gb-boot-sweep 1600ms linear infinite;
}

@keyframes gb-boot-sweep {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(357%);
  }
}

/*
 * Reduced motion: the block goes, the track stays.
 *
 * The blanket rule in `tokens.css` collapses every animation to one 0.01ms
 * iteration, which for an infinite sweep means the block lands wherever that
 * leaves it and sits there — a bar stopped a third of the way across, which is
 * a progress claim, and a false one. Removing it leaves a plain neutral rule
 * along the bottom edge: furniture, saying nothing.
 *
 * The mark stops too (`brand.module.css` decides what it rests as), so the
 * screen is entirely still. **The rotating tip is what keeps it from reading as
 * frozen** — text changing every few seconds is not motion in the sense this
 * media query is about, and it is the only sign of life left.
 */
@media (prefers-reduced-motion: reduce) {
  .bar i {
    display: none;
  }

  .tip {
    animation: none;
  }
}
